import React from "react";
import { styled } from "@linaria/react";
import t from "../../../../../i18n";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import type { InstrumentationIndicator, InstrumentationState, QuantityInvalidity } from "./utils";
import {
  defineInstrumentationIndicators,
  countInstrumentationIndicators,
  instrumentationIndicatorCountLabels,
  instrumentationIndicatorLabels,
} from "./utils";
import WidgetCard from "@ui/WidgetCard";
import Statistic from "@ui/Statistic";
import type { ColumnMetaData } from "@ui/MiniTable";
import useSortedData from "../../../../../lib/hooks/useSortedData";
import createGlobalState from "@ui/lib/createGlobalState";
import createIOSortType from "../../../../../lib/createIOSortType";
import Table from "@ui/Table";
import { color, fonts, lineHeights } from "@ui";
import { css } from "@linaria/core";
import { useMetrologyOverrides } from "../../../../../api/useMetrologyOverrides";
import Switch from "@ui/Switch";
import * as io from "io-ts";
import { useQuantityIndex, type Quantity } from "../../../../../api/useQuantities";

const InstrumentationWidgetCard = styled(WidgetCard)`
  display: flex;
  flex-direction: column;
  height: max-content;
`;

const WidgetContainer = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0px;
`;

const StyledTable = styled.div`
  position: relative;

  table thead {
    position: sticky;
    top: 0;
    z-index: 1;
    tr {
      th {
        span {
          color: var(--carbon800);
          font: ${fonts.body2};
        }
        background-color: ${color.titanium100} !important;
        min-height: 32px !important;
      }
    }
  }

  table tbody {
    font: ${fonts.body1};
    tr {
      min-height: fit-content;
      td {
        min-height: fit-content;
        padding: 12px;
        font: ${fonts.body1};
        line-height: ${lineHeights[1]};
        color: ${color.carbon900};
      }
    }
  }
`;

const StatisticContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  grid-gap: 24px;
`;
const statisticsStyled = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Position = styled.div`
  margin-left: auto;
  /* position: absolute;
  right: 0; */
`;

interface InstrumentationProps {
  analysis: ActiveAnalysis;
  quantities: Quantity[];
  projectId: string;
}

type InstrumentationTableColumns = "name" | "state";

export const columns: InstrumentationTableColumns[] = ["name", "state"];

const useShowInstrumentationDetails = createGlobalState({
  storage: "session",
  key: "show-instrumentation-details",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

const useSort = createGlobalState({
  storage: "session",
  key: "instrumentation-sort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "name",
    direction: "asc",
  },
  scoped: false,
});

function InstrumentationWidget({ projectId, analysis, quantities }: InstrumentationProps) {
  const metrologyOverrides = useMetrologyOverrides(projectId);
  const quantityIndex = useQuantityIndex(projectId);

  const { indicators, indicatorCount } = React.useMemo(() => {
    const instrumentationIndicators = defineInstrumentationIndicators(
      analysis,
      quantities,
      metrologyOverrides,
      quantityIndex,
    );
    const indicatorCount = countInstrumentationIndicators(instrumentationIndicators);
    return { indicators: instrumentationIndicators, indicatorCount };
  }, [analysis, quantities, metrologyOverrides, quantityIndex]);

  const defineIndicatorLabel = React.useCallback(
    (type: InstrumentationState | QuantityInvalidity) => {
      const label =
        instrumentationIndicatorLabels[
          type as
            | Exclude<InstrumentationState, InstrumentationState.INVALID_MEASURES>
            | QuantityInvalidity
        ];
      if (typeof label === "function") return label(1);
      return label;
    },
    [],
  );

  const [sort, setSort] = useSort({});

  const sortedIndicators = useSortedData(
    indicators,
    d => {
      if (sort.column === "name") return d.quantityName;
      return defineIndicatorLabel(d.type);
    },
    sort.direction,
  );

  const tableMetadata: {
    [key in (typeof columns)[number]]: ColumnMetaData<InstrumentationIndicator>;
  } = React.useMemo(
    () => ({
      name: {
        label: t("summary.instrumentation.table.column.name"),
        maxWidth: "250px",
        width: "50%",
        render: ({ quantityName }) => quantityName,
        sortable: true,
      },
      state: {
        label: t("summary.instrumentation.table.column.state"),
        width: "50%",
        render: ({ type }) => defineIndicatorLabel(type),
        sortable: true,
      },
    }),
    [defineIndicatorLabel],
  );

  const [withDetails, setWithDetails] = useShowInstrumentationDetails({
    scope: projectId,
  });

  return (
    <InstrumentationWidgetCard
      title={t("summary.instrumentation.title")}
      actions={
        <Position>
          <Switch
            position="right"
            checked={sortedIndicators.length === 0 ? false : withDetails}
            onChange={e => setWithDetails(e.target.checked)}
            disabled={sortedIndicators.length === 0}
          >
            {t("summaryPage.widgets.losses.switch.label")}
          </Switch>
        </Position>
      }
    >
      <WidgetContainer>
        <StatisticContainer>
          {indicatorCount.map(([indicator, count]) => (
            <Statistic
              key={indicator}
              title={instrumentationIndicatorCountLabels[
                indicator as keyof typeof instrumentationIndicatorCountLabels
              ](count)}
              value={count}
              className={statisticsStyled}
            />
          ))}
        </StatisticContainer>
        {withDetails && sortedIndicators.length > 0 && (
          <StyledTable>
            <Table<InstrumentationIndicator, InstrumentationTableColumns>
              datas={sortedIndicators}
              sort={sort}
              onSort={setSort}
              metadata={tableMetadata}
              computeRowKey={({ quantityName }) => quantityName}
            />
          </StyledTable>
        )}
      </WidgetContainer>
    </InstrumentationWidgetCard>
  );
}

export default React.memo(InstrumentationWidget);
